import React, { useEffect, useState } from 'react';

import { Button } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { Combobox } from '@eika/combobox';
import { Label } from '@eika/label';

import useTranslation from '../../../../hooks/useTranslation';
import { formatCurrencyLabel } from '../currencyUtils';
import { CrossBorderPaymentActionType } from '../form/actions';
import useFormContext from '../form/useFormContext';
import CurrencySelectSkeleton from './CurrencySelectSkeleton';
import useUniqueCurrencies from './useUniqueCurrencies';

import './CurrencySelect.scss';

interface Props {
    showKinaInfo?: boolean;
}

const CurrencySelect: React.FC<Props> = ({ showKinaInfo }) => {
    const translated = useTranslation();
    const { handleChange } = useFormContext();
    const [currencyText, setCurrencyText] = useState<string>('');

    const { currencies, isLoading, error, refetch, countryCurrency, eurCurrency } = useUniqueCurrencies();

    const [isInvalidCurrency, setIsInvalidCurrency] = useState(false);

    useEffect(() => {
        if (countryCurrency) {
            setCurrencyText(formatCurrencyLabel(countryCurrency));
        } else {
            setCurrencyText(formatCurrencyLabel(eurCurrency));
        }
    }, [countryCurrency, eurCurrency]);

    if (error) {
        return (
            <ErrorCard
                className="currency-select__fetch-currency-error"
                title={translated.errors.somethingWentWrong}
                description={translated.currencyField.errorTransferCurrency}
            >
                <Button size="small" onClick={() => refetch()}>
                    {translated.tryAgain}
                </Button>
            </ErrorCard>
        );
    }

    if (isLoading) {
        return <CurrencySelectSkeleton />;
    }

    return (
        <div className="currency-select__search-select">
            {currencies && (
                <>
                    <Combobox
                        id="currency-select"
                        label={translated.currencyField.label}
                        suggestions={currencies?.map((currency) => ({
                            label: formatCurrencyLabel(currency),
                            value: currency.currency.toString(),
                        }))}
                        value={currencyText}
                        onChange={setCurrencyText}
                        onFocus={() => setIsInvalidCurrency(false)}
                        onSelect={(currency) => {
                            setIsInvalidCurrency(false);
                            const targetCurrency = currencies?.find((x) => x.currency === currency);
                            if (!targetCurrency) {
                                setIsInvalidCurrency(true);
                                return;
                            }
                            handleChange({
                                type: CrossBorderPaymentActionType.CURRENCY,
                                payload: targetCurrency?.currency ?? currency,
                            });
                            setCurrencyText(formatCurrencyLabel(targetCurrency));
                        }}
                        searchInput
                        hideSearchIcon
                        data-testid="currency-select-input"
                        error={isInvalidCurrency ? translated.crossBorderPayment.invalidCurrency : undefined}
                    />
                    {showKinaInfo && (
                        <Label className="currency-field__kina-label" text={translated.crossBorderPayment.kinaInfo} />
                    )}
                </>
            )}
        </div>
    );
};

export default CurrencySelect;
